/**
 * Locale/currency mapping for Intl.NumberFormat (display only).
 * Payloads should still use numeric amounts and ISO currency codes.
 */
const CURRENCY_LOCALE_MAP = Object.freeze({
  USD: { locale: "en-US", currency: "USD" },
  MXN: { locale: "es-MX", currency: "MXN" },
  BRL: { locale: "pt-BR", currency: "BRL" },
  PEN: { locale: "es-PE", currency: "PEN" },
  COP: { locale: "es-CO", currency: "COP" },
  VES: { locale: "es-VE", currency: "VES" },
});

const FALLBACK = Object.freeze({ locale: "en-US", currency: "USD" });

const formatterCache = new Map();

/**
 * @param {string} [currencyCode]
 * @returns {{ locale: string, currency: string }}
 */
export function getCurrencyFormatConfig(currencyCode) {
  const upper = (currencyCode || "").trim().toUpperCase();
  return CURRENCY_LOCALE_MAP[upper] || FALLBACK;
}

function getFormatter(locale, currency) {
  const key = `${locale}|${currency}`;
  if (!formatterCache.has(key)) {
    formatterCache.set(
      key,
      new Intl.NumberFormat(locale, { style: "currency", currency })
    );
  }
  return formatterCache.get(key);
}

/**
 * @param {unknown} value
 * @param {string} [currencyCode]
 * @param {{ emptyDisplay?: string }} [options]
 * @returns {string}
 */
export function formatCurrencyAmount(value, currencyCode, options = {}) {
  const { emptyDisplay = "-" } = options;
  if (value === "" || value == null) return emptyDisplay;
  const num = typeof value === "number" ? value : parseFloat(value);
  if (!Number.isFinite(num)) return emptyDisplay;
  const { locale, currency } = getCurrencyFormatConfig(currencyCode);
  return getFormatter(locale, currency).format(num);
}

/**
 * Plain decimal string for editing (no grouping), avoids scientific notation for display in inputs.
 * @param {unknown} value
 * @returns {string}
 */
export function amountToPlainInputString(value) {
  if (value === "" || value == null) return "";
  const n = typeof value === "number" ? value : parseFloat(value);
  if (!Number.isFinite(n)) return "";
  const s = String(n);
  if (/e/i.test(s)) {
    return n.toLocaleString("en-US", {
      useGrouping: false,
      maximumFractionDigits: 20,
    });
  }
  return s;
}

/**
 * Parses user input for amount fields; strips currency symbols and grouping commas (US-style).
 * @param {unknown} raw
 * @returns {number}
 */
export function parseAmountInput(raw) {
  if (raw == null) return NaN;
  const s = String(raw).trim();
  if (s === "") return NaN;
  const cleaned = s.replace(/[^\d.,-]/g, "").replace(/,/g, "");
  const num = parseFloat(cleaned);
  return Number.isFinite(num) ? num : NaN;
}

export { CURRENCY_LOCALE_MAP, FALLBACK };
